import React, { useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { getBatteryOemsReq } from "@/api/userApi";
import { getBatteryListReq } from "@/api/batteryApi";
import { SizingFormData } from "../types";
import {
  CustomerInformationSection,
  SystemSpecificationsSection,
  BatteryCalculationsSection,
  SizingToolActions,
} from "@/components/SizingTool";

interface SizingToolFormManualProps {
  sizingForm: SizingFormData;
  setSizingForm: (form: SizingFormData) => void;
  handleAnalyze: (formValues?: SizingFormData) => void;
  analyzing: boolean;
  sizingSubmitted?: number;
  setOpenSizingTool: (open: boolean) => void;
  useForm: any;
  handleSizingInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  getBatteryOems?: (searchValue: string) => Promise<any>;
  getBatteryList?: (searchValue: string) => Promise<any>;
  onCustomerDataPopulated?: (
    accountNumber: string,
    customerName: string
  ) => void;
  handleCloseRequest?: () => void;
  showNewFields?: boolean;
  showSolarSystemCheckboxes?: boolean;
  projectData?: any;
}

export const SizingToolFormManual: React.FC<SizingToolFormManualProps> = ({
  sizingForm,
  setSizingForm,
  handleAnalyze,
  analyzing,
  sizingSubmitted = 0,
  setOpenSizingTool,
  useForm: useFormHook,
  getBatteryOems = getBatteryOemsReq,
  handleCloseRequest,
  showNewFields = false,
  showSolarSystemCheckboxes = false,
  projectData,
}) => {
  const formInstance =
    typeof useFormHook === "function"
      ? useFormHook({
          defaultValues: sizingForm,
          mode: "onSubmit",
          reValidateMode: "onChange",
        })
      : useFormHook;

  const batteryManufacturerId = formInstance.watch("batteryManufacturerId");
  const hasExistingBattery = formInstance.watch("hasExistingBattery");
  const checkFieldsDisabled = analyzing || Boolean(sizingSubmitted);
  const fieldsDisabled = Boolean(projectData) || analyzing || Boolean(sizingSubmitted);

  useEffect(() => {
    formInstance.trigger();
  }, [formInstance]);

  const fetchBatteryData = useCallback(
    async (selectedBatteryName: string) => {
      if (!batteryManufacturerId) return;

      try {
        const response = await getBatteryListReq(
          String(batteryManufacturerId),
          selectedBatteryName
        );

        const batteryModels = response?.data?.batteryModels || [];
        const selectedBattery = batteryModels.find(
          (battery: any) => battery.name === selectedBatteryName
        );

        if (selectedBattery) {
          if (selectedBattery.nominalPowerKw) {
            formInstance.setValue(
              "nominalPower",
              selectedBattery.nominalPowerKw
            );
            setSizingForm({
              ...sizingForm,
              nominalPower: selectedBattery.nominalPowerKw,
            });
            formInstance.trigger("nominalPower");
          }

          let capacityValue =
            selectedBattery.totalCapacityKwh ||
            selectedBattery.capacity ||
            selectedBattery.usableCapacity;

          if (capacityValue) {
            formInstance.setValue("usableCapacity", capacityValue);
            setSizingForm({
              ...sizingForm,
              usableCapacity: capacityValue,
            });
            formInstance.trigger("usableCapacity");
          }
        }
      } catch (error) {
        console.error("Failed to fetch battery data:", error);
      }
    },
    [batteryManufacturerId, formInstance, setSizingForm, sizingForm]
  );

  const handleSubmit = formInstance.handleSubmit(
    (formValues: SizingFormData) => {
      if (formValues?.hasExistingBattery) return;
      handleAnalyze(formValues);
    }
  );

  const handleCancel = () => {
    if (handleCloseRequest) {
      handleCloseRequest();
    } else {
      setOpenSizingTool(false);
    }
  };

  return (
    <FormProvider {...formInstance}>
      <Box
        component="form"
        sx={{ width: "100%" }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <CustomerInformationSection
          formInstance={formInstance}
          sizingForm={sizingForm}
          setSizingForm={setSizingForm}
          disabled={fieldsDisabled}
          isSizingTool={true}
        />

        <SystemSpecificationsSection
          formInstance={formInstance}
          sizingForm={sizingForm}
          setSizingForm={setSizingForm}
          disabled={fieldsDisabled}
          checkFieldsDisabled={checkFieldsDisabled}
          getBatteryOems={getBatteryOems}
          getBatteryList={getBatteryListReq}
          onBatteryProductChange={fetchBatteryData}
          showNewFields={showNewFields}
          showSolarSystemCheckboxes={showSolarSystemCheckboxes}
        />

        <BatteryCalculationsSection formInstance={formInstance} />

        <SizingToolActions
          analyzing={analyzing}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isValid={formInstance.formState.isValid}
          disableAnalyze={!!hasExistingBattery}
        />
      </Box>
    </FormProvider>
  );
};

export default SizingToolFormManual;
